import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Youtube } from "lucide-react";
import portrait from "@/assets/david-isei-portrait.jpg";

const VIDEO_ID = "YrM7aRqypm8";

function IntroVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <figure className="w-full max-w-[340px] mx-auto lg:mx-0">
      <div className="relative aspect-[4/5] overflow-hidden rounded-lg border border-border/50 bg-card shadow-2xl">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
            title="David Isei: 90-second introduction"
            className="absolute inset-0 h-full w-full"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
          >
            <img
              src={portrait}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
              loading="eager"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
            <span className="absolute bottom-0 left-0 right-0 flex items-center gap-3 p-5">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg transition-transform group-hover:scale-105">
                <Play className="h-5 w-5 translate-x-0.5" fill="currentColor" aria-hidden="true" />
              </span>
              <span className="text-[15px] font-semibold leading-snug text-foreground">
                Watch my 90-second introduction
              </span>
            </span>
          </button>
        )}
      </div>
      <figcaption className="mt-3 text-center">
        <a
          href="https://www.youtube.com/@ISEIInsights"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-accent"
        >
          <Youtube className="h-4 w-4" aria-hidden="true" />
          More videos on YouTube
        </a>
      </figcaption>
    </figure>
  );
}

export function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 lg:py-20 relative z-10 w-full">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h1 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground leading-[1.1] mb-6 tracking-tight">
              Making Hawaiʻi’s healthcare system easier to understand—and improve.
            </h1>
            <p className="text-lg text-foreground/85 leading-relaxed mb-8 max-w-xl">
              I’m David Isei, Executive Director of the Hawaiʻi Healthcare Task Force. I turn complex healthcare records into clear research on access, workforce, and policy—with transparent sources and published corrections.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-7 h-12 text-base">
                <Link to="/articles">
                  Explore the research
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-foreground/30 bg-transparent text-foreground hover:bg-muted font-semibold px-7 h-12 text-base">
                <Link to="/connect">Request a briefing</Link>
              </Button>
            </div>
            <p className="mt-6 text-sm text-foreground/75 max-w-xl">
              Every figure carries a source, a date and a label.{" "}
              <Link to="/dashboard" className="text-accent underline underline-offset-4 hover:opacity-80">How figures are labeled</Link>
              {" · "}
              <Link to="/corrections" className="text-accent underline underline-offset-4 hover:opacity-80">Corrections log</Link>
            </p>
            <p className="mt-8 border-t border-border/40 pt-5 text-sm text-foreground/75">
              Executive Director, Hawaiʻi Healthcare Task Force · U.S. Air Force veteran ·{" "}
              <Link to="/about" className="text-accent underline underline-offset-4 hover:opacity-80">Full background</Link>
            </p>
          </motion.div>

          <IntroVideo />
        </div>
      </div>
    </section>
  );
}
